import * as jud from "@/services/jud.service";
import { idbGet, idbPut, idbDelete, VIDEO_KEY } from "@/services/idb";
import { clearProgress } from "@/services/progress";
import { session } from "@/services/session";
import { blobToDataUrl } from "@/utils/media";
import { FLOW_ORDER } from "@/constants";

/**
 * The three eKYC artefacts and where the user is in collecting them.
 *
 * `face`, `signature` and `videoPoster` are small data-URLs and live here so the review
 * screen can render them directly. The recorded video is a blob in IndexedDB and only
 * `hasVideo` is tracked here — a multi-megabyte base64 string in reactive state was the
 * old design's worst memory problem, and it did not survive a reload anyway.
 */
export default {
  namespaced: true,

  state: () => ({
    face: "",
    signature: "",
    videoPoster: "",
    hasVideo: false,
    challengeSequence: "",
    /** Route name of the furthest step reached, for resume and the order guard. */
    resumeAt: "",
    trackingId: "",
    /** Submitted successfully. Keeps /complete reachable after the media is dropped. */
    completed: false,
  }),

  getters: {
    faceReady: (state) => !!state.face,
    videoReady: (state) => state.hasVideo,
    signatureReady: (state) => !!state.signature,

    /** All three artefacts present — the precondition for submitting. */
    isComplete: (state) => !!state.face && !!state.signature && state.hasVideo,

    /** Reachability of /complete: either ready to submit, or already submitted. */
    canShowResult: (state, getters) => getters.isComplete || state.completed,

    /**
     * How far the user is allowed to navigate. A step is reachable once every step
     * before it in FLOW_ORDER has produced its artefact.
     */
    furthestStep: (state) => {
      if (!state.face) return FLOW_ORDER[0];
      if (!state.hasVideo) return FLOW_ORDER[1];
      if (!state.signature) return FLOW_ORDER[2];
      return FLOW_ORDER[3];
    },

    /** Is there enough saved work to be worth offering a resume? */
    canResume: (state) => !!state.face && !!state.resumeAt,
  },

  mutations: {
    SET_FACE(state, dataUrl) {
      state.face = dataUrl || "";
    },
    SET_SIGNATURE(state, dataUrl) {
      state.signature = dataUrl || "";
    },
    SET_VIDEO(state, { hasVideo, videoPoster }) {
      state.hasVideo = !!hasVideo;
      state.videoPoster = videoPoster || "";
    },
    SET_CHALLENGE(state, sequence) {
      state.challengeSequence = sequence || "";
    },
    SET_RESUME_AT(state, routeName) {
      state.resumeAt = routeName || "";
    },
    SET_TRACKING_ID(state, id) {
      state.trackingId = id || "";
    },
    /** Drop the captured media but keep the outcome — used once the submit has landed. */
    CLEAR_MEDIA(state) {
      state.face = "";
      state.signature = "";
      state.videoPoster = "";
      state.hasVideo = false;
      state.challengeSequence = "";
      state.resumeAt = "";
      state.completed = true;
    },
    RESET(state) {
      state.face = "";
      state.signature = "";
      state.videoPoster = "";
      state.hasVideo = false;
      state.challengeSequence = "";
      state.resumeAt = "";
      state.trackingId = "";
      state.completed = false;
    },
    /** Merge a persisted slice back in on boot. */
    HYDRATE(state, saved) {
      Object.keys(saved || {}).forEach((key) => {
        if (key in state) state[key] = saved[key];
      });
    },
  },

  actions: {
    /** Ask the backend for the phrase the user must read on camera. */
    async loadChallenge({ commit, state }) {
      if (state.challengeSequence) return state.challengeSequence;
      const { challengeSequence } = await jud.selectChallenge();
      commit("SET_CHALLENGE", challengeSequence);
      return challengeSequence;
    },

    /** Park the recorded clip in IndexedDB and keep only a poster frame in state. */
    async saveVideo({ commit }, { blob, poster }) {
      if (blob) await idbPut(VIDEO_KEY, blob);
      commit("SET_VIDEO", { hasVideo: !!blob, videoPoster: poster });
    },

    /** Retrieve the recorded clip — for playback on review, or for submitting. */
    loadVideoBlob() {
      return idbGet(VIDEO_KEY);
    },

    /**
     * The one real network moment.
     *
     * The video is read back from IndexedDB and base64'd here rather than being held in
     * memory for the whole flow. On failure nothing is cleared — the user keeps every
     * artefact and can retry once connectivity is back.
     */
    async submit({ state, commit }) {
      const blob = await idbGet(VIDEO_KEY);
      const video = blob ? await blobToDataUrl(blob) : "";

      const result = await jud.finalize({
        face: state.face,
        signature: state.signature,
        video,
        judAccessToken: session.accessToken(),
      });

      const trackingId = (result && result.trackingId) || session.trackingId() || "";
      commit("SET_TRACKING_ID", trackingId);
      return { trackingId };
    },

    /**
     * The submit landed.
     *
     * The captured face, signature and video are personal data with no remaining purpose,
     * so they are dropped from memory and from both storage tiers immediately — and it
     * also means no stale resume can be offered for a flow that is already finished. The
     * tracking id stays: the done screen shows it.
     */
    async finish({ commit }) {
      await clearProgress();
      commit("CLEAR_MEDIA");
    },

    /** Explicit "start over", session expiry, or a restart after a resume prompt. */
    async reset({ commit }) {
      await clearProgress();
      await idbDelete(VIDEO_KEY);
      commit("RESET");
    },
  },
};
